import { useContext, useEffect, useMemo, useState } from "react";
import { BoxFull, TransactionalSubheader, Dropdown, Button, AccessButton, InputText } from "iuvity-ui-components";
import { MultiSelect } from "primereact/multiselect";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/index";
import { DescriptionBox } from "../../components/pageComponents/DescriptionBox";
import { Controller, useForm, useWatch } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { ALIAS_PARENT, REVALIDATE_FORM_PROPS } from "../../constants";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Column } from "iuvity-ui-components/dist/column";
import { useConsoleFunctionality } from "../../hooks/useConsoleFunctionalities";
import { AuditReportFormSchema } from "../../validations/schemas";
import { getUsers } from "../../services/iam/users";
import { getRolesByTenant } from "../../services/console/roles";
import { postAuditReport } from "../../services/audit/audit";
import { Calendar } from "primereact/calendar";
import { AuditoryDetail } from "./AuditoryDetail";
import { Actions } from "../../components/datatable/Actions";
import {
    CustomDialog,
    DialogProps,
  } from "../../components/dialog/CustomDialog";
  import { formatDateToLocale } from "../../helpers/formatDate";
import { useUserRole } from "../../hooks/useUserRole";
import { exportAuditReportPdf, exportAuditReportCsv, getAuditReportDetail } from "../../services/audit/audit";
import { EmptyTemplate } from "../../components/datatable/EmptyTemplate";
import noResultsImage from "../../assets/Illustration_NoResults.svg";

interface InumSelectedItems {
    tenant: number,
    name: number,
    role: number,
    functionality: number,
};

interface IMultiselectOptions {
    label: string,
    value: string,
    id?: string,
    alias?: string,
    keylabel?: string;
};

interface IDefaultFormValues {
    period: string | undefined,
    tenant: string[] | undefined,
    name: string[] | undefined,
    role: string[] | undefined,
    functionality: string[] | undefined,
};

export const AuditoryReportPage = () => {
    const navigate = useNavigate();
    const {
        t,
        appState: { user, childrenTenants, tenantParentName, aliasParentTenant,
            tenantId, selectedTenantId, language },
        toast,
    } = useContext(AppContext);

    const periodOptions = [
        {label: t('ConsoleTranslations.last7daysLabel'), value: '1'},
        {label: t('ConsoleTranslations.lastWeekLabel'), value: '2'},
        {label: t('ConsoleTranslations.previousMonthLabel'), value: '3'},
        {label: t('ConsoleTranslations.last30daysLabel'), value: '4'},
        {label: t('ConsoleTranslations.dateRangeLabel'), value: '5'}
    ]

    // Data de permisos por role actual
    const { data: dataRolePermissions } = useUserRole("DATA");
    const [permissionsInfoByRole, setPermissionsInfoByRole] = useState({
        CREATE: false,
        READ: false,
        UPDATE: false,
        REMOVE: false,
    });
    useEffect(() => {
        if (dataRolePermissions) {
            let data = dataRolePermissions[0]?.rolePermissions;
            const permissions = {
                CREATE: !!data?.find((rol: any) => (rol?.functionality?.code === "COD_AREPM" && rol.permission.privilegeId === "CREATE")),
                READ: !!data?.find((rol: any) => (rol?.functionality?.code === "COD_AREPM" && rol.permission.privilegeId === "READ")),
                UPDATE: !!data?.find((rol: any) => (rol?.functionality?.code === "COD_AREPM" && rol.permission.privilegeId === "UPDATE")),
                REMOVE: !!data?.find((rol: any) => (rol?.functionality?.code === "COD_AREPM" && rol.permission.privilegeId === "REMOVE")),
            };
            (JSON.stringify(permissionsInfoByRole) !== JSON.stringify(permissions)) &&
                setPermissionsInfoByRole(permissions);
        }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [dataRolePermissions]);

    // Data de funcionalidades, parseo y ordenamiento
    const { data: dataFunctionalities } = useConsoleFunctionality("DATA");
    const functionalitiesListWithDuplicates = dataFunctionalities?.map((functionality: any) => {
        return {
            label: t(`ConsoleTranslations.${functionality.keylabel}`),
            value: t(`ConsoleTranslations.${functionality.keylabel}`),
            id: functionality.auditcode,
            keylabel: functionality.keylabel,
        }})
        ?.filter((functionality: any) => !!functionality?.id)
        ?.sort((a: any, b: any) => a.label.localeCompare(b.label));

    const [showDetail, setShowDetail] = useState<boolean>(false);
    const [selectedAuditoryRegister, setSelectedAuditoryRegister] = useState<{}>({});
    // const [dataFiltered, setDataFiltered] = useState<any[] | null>(null); *******************************************************************
    const [dataToReport, setDataToReport] = useState<any>(null);
    const [tenantsFilled, setTenantsFilled] = useState<IMultiselectOptions[]>([]);

    useEffect(() => {
            setTenantsFilled(permissionsInfoByRole.READ
                ? ( !!childrenTenants
                    ? [
                        {
                            label: tenantParentName ?? '',
                            value: tenantParentName ?? '',
                            id: selectedTenantId ?? tenantId,
                            alias: aliasParentTenant,
                        },
                        ...childrenTenants?.map((tenant:any)=>{
                        return {
                            label: tenant?.name,
                            value: tenant?.name,
                            id: tenant?.id,
                            alias: tenant?.alias,
                        }})]
                    : [{
                        label: tenantParentName ?? '',
                        value: tenantParentName ?? '',
                        id: selectedTenantId ?? tenantId,
                        alias: aliasParentTenant,
                    }])
                : [{
                    label: tenantParentName ?? '',
                    value: tenantParentName ?? '',
                    id: selectedTenantId ?? tenantId,
                    alias: aliasParentTenant,
                }],
            );
    }, [aliasParentTenant, childrenTenants, permissionsInfoByRole.READ, selectedTenantId, tenantId, tenantParentName]);

    // State para el rango de fechas
    const [showDateRange, setShowDateRange] = useState<boolean>(false);
    const [period, setPeriod] = useState<{
        startDate: {
            value:  Date | undefined,
            maxDate:  Date | undefined,
            minDate:  Date | undefined,
        },
        endDate: {
            value:  Date | undefined,
            maxDate:  Date | undefined,
            minDate:  Date | undefined,
        },
    }>({
        startDate: {
            value: undefined,
            maxDate: new Date(),
            minDate: new Date(new Date(new Date()).setDate((new Date()).getDate() - 60)),
        },
        endDate: {
            value: undefined,
            maxDate: new Date(),
            minDate: new Date(new Date(new Date()).setDate((new Date()).getDate() - 60)),
        },
    });

    // States de Options para los Multiselects
    const [functionalityOptions, setFunctionalityOptions] = useState<IMultiselectOptions[]>([]);
    const [nameOptions, setNameOptions] = useState<any[]>([]);
    const [roleOptions, setRoleOptions] = useState<any[]>([]);
    const [tenantOptions, setTenantOptions] = useState<any[]>([]);
    const [nameAndRoleOptionsOrigin, setNameAndRoleOptionsOrigin] = useState<{
        name: any[],
        role: any[],
    }>({
        name: nameOptions,
        role: roleOptions,
    });
    const [disableButton, setDisableButton] = useState<boolean>(false);

    useEffect(()=>{
        if(tenantOptions.toString() !== tenantsFilled.toString()) {
            setTenantOptions(tenantsFilled);
            setValue('tenant', tenantsFilled?.map((tenant) => tenant?.value));
            setNumSelectedItems((prev: InumSelectedItems)=>({
                ...prev,
                tenant: tenantsFilled.length,
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[tenantsFilled]);

    const aliasParentOrigin = sessionStorage.getItem(ALIAS_PARENT);
    // Efecto para obtención de roles de todos los tenants
    useEffect(() => {
        if ((!permissionsInfoByRole.READ && tenantOptions?.length === 1)
        || (permissionsInfoByRole.READ && tenantOptions?.length > 0)) {
            const tenantIds = tenantOptions?.map((tenant)=>tenant?.id);
            let userListAllTenants: any[] = (
                (!permissionsInfoByRole.READ && aliasParentTenant === aliasParentOrigin) ||
                (aliasParentTenant !== aliasParentOrigin))
                ? [{
                    id: user?.userId,
                    label: `${user?.name} ${user?.lastname} - ${aliasParentOrigin}`,
                    value: `${user?.name} ${user?.lastname} - ${aliasParentOrigin}`}]
                : [];
            let roleListAllTenants: any[] = [];
            (async () => {
                try {
                    if (permissionsInfoByRole.READ) {
                        const { data: dataUsers } = await getUsers(tenantIds.toString());
                        const users: any[] = dataUsers?.filter((user:any)=>user.status !== 'DELETED')
                        ?.map((user:any)=>{
                            return {
                                label: `${user.firstName} ${user.lastName} - ${user.tenantAlias}`,
                                value: `${user.firstName} ${user.lastName} - ${user.tenantAlias}`,
                                id: user.id,
                            }
                        });
                        userListAllTenants = [ ...userListAllTenants, ...users];
                    }

                    const { data: dataRoles } = await getRolesByTenant(tenantIds.toString());
                    const roles: any[] = dataRoles?.filter((role:any)=>role.statusId !== 'DELETED')
                    ?.map((role:any)=>({
                        label: `${role.name} - ${tenantOptions.find((tenant) => tenant.id === role.tenantId).alias}`,
                        value: `${role.name} - ${tenantOptions.find((tenant) => tenant.id === role.tenantId).alias}`,
                        id: role.id,
                    }));

                    roleListAllTenants = [ ...roleListAllTenants, ...roles];
                    setNameOptions(userListAllTenants);
                    setRoleOptions(roleListAllTenants);
                    setNumSelectedItems((prev: InumSelectedItems)=>({
                        ...prev,
                        name: 1,
                        role: roleListAllTenants.length,
                    }));
                    setNameAndRoleOptionsOrigin((prev)=>({
                        ...prev,
                        name: userListAllTenants,
                        role: roleListAllTenants,
                    }));
                    setValue('name', [`${user?.name} ${user?.lastname} - ${aliasParentOrigin}`]);
                    setValue('role', roleListAllTenants?.map((role) => role?.value));

                } catch (error: any) {
                    toast.current.replace({
                        severity: "error",
                        summary: "",
                        detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
                        life: 3000,
                    });
                };
            })();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[permissionsInfoByRole.READ, tenantOptions]);

    // States para control de items seleccionados para las props del Multiselect
    const [numSelectedItems, setNumSelectedItems] = useState<InumSelectedItems>({
        tenant: 0,
        name: 0,
        role: 0,
        functionality: 0,
    });

    const defaultFormValues: IDefaultFormValues = useMemo(()=>{
        return {
            period: '1',
            tenant: tenantOptions?.map((tenant:IMultiselectOptions)=>tenant?.value),
            name: [`${user?.name} ${user?.lastname} - ${aliasParentOrigin}`],
            role: roleOptions?.map((role)=>role?.value),
            functionality: functionalityOptions?.map((functionality)=>functionality?.value),
        }
    }, [aliasParentOrigin, functionalityOptions, roleOptions, tenantOptions, user?.lastname, user?.name]);
        
    const {
        control,
        setValue,
        getValues,
        setError,
        watch,
        reset,
        formState: { errors, isDirty },
        } = useForm({
        mode: "onChange",
        delayError: 1200,
        reValidateMode: "onChange",
        defaultValues: defaultFormValues,
    });

    useEffect(()=>{
        watch((value)=>{
          if (value.period === '5') {
            setShowDateRange(true);
          } else {
            setShowDateRange(false);
            setPeriod((prev)=>({
                ...prev,
                startDate: {
                    ...prev.startDate,
                    value: undefined,
                },
                endDate: {
                    ...prev.endDate,
                    value: undefined,
                }
            }));
          };
          if ((value.tenant ?? [])?.length > 0 && (value.name ?? [])?.length > 0 && (value.role ?? [])?.length > 0
                && (value.functionality ?? [])?.length > 0) {
            setDisableButton(false);
          }
          else {
            setDisableButton(true);
          };
          if (!!dataToReport && !sessionStorage.getItem('INIT::FILTER')) {
            setRecords(null);
            setDataToReport(null);
            setTotalRecords(0);
            setFirst(0);
            setRows(10);
          };
        });
      },[dataToReport, watch]);

    const periodValues = useWatch({
        control,
        name: "period",
    });

    const tenantValues = useWatch({
        control,
        name: "tenant",
    });

    const nameValues = useWatch({
        control,
        name: "name",
    });

    const roleValues = useWatch({
        control,
        name: "role",
    });

    const functionalityValues = useWatch({
        control,
        name: "functionality",
    });

    useEffect(()=>{
        const allFieldsFilled = (getValues('tenant') ?? [])?.length > 0 && (getValues('name') ?? [])?.length > 0
            && (getValues('role') ?? [])?.length > 0 && (getValues('functionality') ?? [])?.length > 0;
        if (periodValues === '5' && allFieldsFilled) {
            setDisableButton((prev) => !(!!period.startDate.value && !!period.endDate.value));
        };
        if (!!dataToReport && !sessionStorage.getItem('INIT::FILTER')) {
            setRecords(null);
            setDataToReport(null);
            setTotalRecords(0);
            setFirst(0);
            setRows(10);
          };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[period.startDate.value, period.endDate.value, periodValues]);

    // Efectos para actualizar la data de Options del multiselect con Promesas
    useEffect(()=>{
        let functOptionsParsed = functionalitiesListWithDuplicates?.filter((object: any, currentIndex: any) => {
            return functionalitiesListWithDuplicates.findIndex((duplicateObject: any) =>
                duplicateObject.label === object.label) === currentIndex;
            });
        functOptionsParsed = functOptionsParsed?.map((optionElem:any)=>({
            ...optionElem,
            label: t(`ConsoleTranslations.${optionElem?.keylabel}`),
            value: t(`ConsoleTranslations.${optionElem?.keylabel}`),
        }));
        setFunctionalityOptions(functOptionsParsed);
        setValue('functionality', functOptionsParsed?.map((functionality: IMultiselectOptions)=>functionality.value));
        setNumSelectedItems((prev:InumSelectedItems) => ({
            ...prev,
            functionality: functOptionsParsed?.length,
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[dataFunctionalities, language]);

    // useEffect(()=>{
    //     setValue('tenant', tenantOptions?.map((tenant) => tenant?.value));
    //     setNumSelectedItems((prev: InumSelectedItems)=>({
    //         ...prev,
    //         tenant: tenantOptions.length,
    //     }));
    // // eslint-disable-next-line react-hooks/exhaustive-deps
    // },[tenantOptions]);

    const columns = useMemo(() => {
        if (language) {
            return [
                {
                    field: "date",
                    header: t("ConsoleTranslations.dateLabel"),
                },
                {
                    field: "tenant",
                    header: t("ConsoleTranslations.tenantLabel"),
                },
                {
                    field: "application",
                    header: t("ConsoleTranslations.applicationLabel"),
                },
                {
                    field: "name",
                    header: t("ConsoleTranslations.fullnameLabel"),
                },
                {
                    field: "username",
                    header: t("ConsoleTranslations.usernameLabel"),
                },
                {
                    field: "role",
                    header: t("ConsoleTranslations.roleLabel"),
                },
                {
                    field: "functionality",
                    header: t("ConsoleTranslations.functionalityLabel"),
                },
                {
                    field: "activity",
                    header: t("ConsoleTranslations.activityLabel"),
                },
                {
                    field: "actions",
                    header: t("ConsoleTranslations.actionsLabel"),
                },
            ]
        } else {
            return []
        };
    }, [t, language]);

    const [selectedColumns, setSelectedColumns] = useState(columns);

    useEffect(() => {
        if (language)
            setSelectedColumns((prev) =>
                prev.map((col) => ({
                    field: col.field,
                    header: t(`ConsoleTranslations.${
                        col.field === 'name'
                            ? 'fullname'
                            : col.field
                    }Label`),
                }))
            );
    }, [t, language, setSelectedColumns]);

    // Template para la presentación  de items seleccionados (Multiselect)
    const selectedItemTemplateForAllFields = (
        value: any,
        placeholder: string,
        numSelectedItems: number,
        marginLeft?: string
    ) => {
        return !!value
            ?   <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>{value}{numSelectedItems > 1 && ','}</div>
            :   <div
                    style={{
                        marginLeft: marginLeft ?? undefined,
                        color: 'var(--placeholderColor)',
                    }}
                >{placeholder}</div>
    };
    
    const [modalProps, setModalProps] = useState<DialogProps>({
        visible: false,
    } as DialogProps);

    const onCancel = () => {
        setModalProps({
            visible: false,
        } as DialogProps);
    };

    const onDownloadReport = async (docType: 'CSV' | 'PDF', body: any) => {
        try {
            const fileReport = docType === 'CSV'
                ? await exportAuditReportCsv(dataToReport, language)
                : await exportAuditReportPdf(dataToReport, language);
            let blob = new Blob([fileReport], { type: docType === 'CSV' ? 'text/csv' : 'application/pdf' });
            let filename = `audit-report_${new Date().toISOString().substring(0, 10).replace(/-|:/g, "") +
                "-" + new Date().toLocaleTimeString("es-ES").replace(/-|:/g, "")}.${docType.toLocaleLowerCase()}`;
            let downloadLink = document.createElement("a");
            downloadLink.download = filename;
            downloadLink.href = window.URL.createObjectURL(blob);
            downloadLink.style.display = "none";
            document.body.appendChild(downloadLink);
            downloadLink.click();
        } catch (error: any) {
            toast.current.replace({
                severity: "error",
                summary: "",
                detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
                life: 3000,
            });
        };
    };

    const handleModalProps = (actionType: "DOWNLOAD" | "CANCEL") => {
    switch (actionType) {
        case "DOWNLOAD":
        return setModalProps((prev) => ({
            ...prev,
            visible: true,
            title: t(`ConsoleTranslations.downloadAuditReportModalTitle`),
            content: '',
            acceptLabel: 'PDF',
            rejectLabel: 'CSV',
            onReject: () => onDownloadReport('CSV', records),
            onConfirm: () => onDownloadReport('PDF', records),
        }));
        case "CANCEL":
        return setModalProps((prev) => ({
            ...prev,
            visible: true,
            title: t("Users.cancelModalTitle"),
            content: t("Users.cancelModalContent"),
            onConfirm: onCancel,
        }));
        default:
        return;
    }
    };

    // ***** BLOQUE DE PAGINACIÓN ***** //
    // ******************************** //
    const [records, setRecords] = useState<null | any[]>(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);

    const onPage = useMemo(()=>{
        return async (event?: any, body?: any) => {
            try {
                let _first = 0;
                let _rows = 10;
                if (!!event) {
                    const { first, rows } = event;
                    _first = first;
                    _rows = rows;
                }
                setFirst(_first);
                setRows(_rows);
                const { data } = await postAuditReport(body ?? dataToReport, language, _first.toString(), _rows.toString());
                setRecords(data?.registers);
                setTotalRecords(data?.count);
            } catch (error:any) {
                toast.current.replace({
                    severity: "error",
                    summary: "",
                    detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
                    life: 3000,
                });
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[dataToReport, language]);

    useEffect(()=>{
        const initStartDate = new Date();
        initStartDate.setDate(new Date().getDate() - 7);
        initStartDate.setHours(0,0,0,0);
        const initDataToReport = {
            userId: [user?.userId],
            startCreationDate: initStartDate.toISOString().slice(0,-1).replace('T', ' '),
            endCreationDate: new Date().toISOString().slice(0,-1).replace('T', ' '),
        };
        setDataToReport(initDataToReport);
        sessionStorage.setItem('INIT::FILTER', 'true');
        onPage(undefined, initDataToReport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[]);

    return  (
        <>
            <TransactionalSubheader
                subheaderTitle={
                    <div style={{ position: "relative", left: "-40px" }}>
                        {
                            showDetail &&
                            <i
                                className="iu iu-icon-left pr-3"
                                onClick={() => setShowDetail(false)}
                                aria-hidden="true"
                                style={{
                                    fontSize: "var(--font-size-tablet-title)",
                                    cursor: "pointer",
                                }}
                            />
                        }
                        {showDetail ? t("GlobalConfigurations.detailTitle") : t("ConsoleTranslations.auditReportTitle")}
                    </div>
                }
            >
                <TransactionalSubheader.BreadCrumb
                id={t("GlobalConfigurations.breadcrumbParentLabel")}
                disabledDefaultHome={true}
                breadcrumbsHomeItem={{
                    icon: "iu iu-icon-home",
                    command: () => navigate("/"),
                }}
                breadcrumbsItems={[
                    {
                    label: t("ConsoleTranslations.accessControlBreadcrumbs"),
                    disabled: true
                    },
                    {
                    label: t("ConsoleTranslations.auditoryReportBreadcrumb"),
                    disabled: true
                    },
                ]}
                />
                <TransactionalSubheader.Title />
            </TransactionalSubheader>
            {
                !showDetail ?
                <>
                    <DescriptionBox
                        label={t('ConsoleTranslations.auditReportDescription')}
                    />
                    <BoxFull
                        className="flex flex-column align-items-center justify-content-center mt-4 pb-0"
                        variant="tertiary"
                    >
                        <div className="flex flex-column">
                            <div className="inputlabel h4 pl-4 mb-4">
                                {t("ConsoleTranslations.reportDataSubTitle")}
                            </div>
                            <div className="form-container md py-5">
                                <div className="" style={{ width: 'var(--inputMaxWidth)' }}>
                                    <Controller
                                        name="period"
                                        control={control}
                                        render={({ field }) => {
                                            return (
                                                <Dropdown
                                                    key='period'
                                                    id='input-period'
                                                    className="mb-3"
                                                    label={t('ConsoleTranslations.periodLabel')}
                                                    placeholder={t('ConsoleTranslations.periodPlaceholderLabel')}
                                                    style={{ width: "100%" }}
                                                    options={periodOptions}
                                                    filter={false}
                                                    showClear={false}
                                                    {...field}
                                                    onChange={(e)=>{
                                                        setValue('period', e?.value);
                                                        if(!!sessionStorage.getItem('INIT::FILTER')) {
                                                                sessionStorage.removeItem('INIT::FILTER');
                                                        };
                                                    }}
                                                    showError={(errors as any)?.period?.message ? true : false}
                                                    errorMessage={(errors as any)?.period?.message}
                                                />
                                            )
                                        }}
                                    />
                                    <ErrorMessage
                                        errors={errors}
                                        name="period"
                                        //@ts-ignore
                                        render={({ message }) => (
                                            <small
                                            className="p-error block"
                                            style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                                            >
                                            {message}
                                            </small>
                                        )}
                                    />

                                    {
                                        showDateRange &&
                                        <div>
                                            <p
                                                style={{
                                                    fontSize: 'var(--font-size-sm)',
                                                    color: 'var(--labelsAndTextColor)',
                                                }}
                                            >
                                                {t("ConsoleTranslations.starDateLabel")}
                                            </p>
                                            <Calendar
                                                id="start-date-audit"
                                                key="start-date-audit"
                                                className="mb-3"
                                                showIcon
                                                icon="iu iu-icon-calendar"
                                                placeholder={language === 'es' ? "DD/MM/YYYY" : "MM/DD/YYYY"}
                                                dateFormat={ language === 'es' ? "dd/mm/yy" : "mm/dd/yy"}
                                                value={period.startDate.value}
                                                onChange={(e: any) => {
                                                    setPeriod((prev)=>({
                                                        ...prev,
                                                        startDate: {
                                                            ...prev.startDate,
                                                            value: e.value,
                                                        },
                                                        endDate: {
                                                            ...prev.endDate,
                                                            value: undefined,
                                                            minDate: e.value,
                                                        }
                                                    }));
                                                }}
                                                hourFormat="12"
                                                minDate={period.startDate.minDate}
                                                maxDate={period.startDate.maxDate}
                                            />
                                            <p
                                                className={!period.startDate.value ? 'p-disabled' : ''}
                                                style={{
                                                    fontSize: 'var(--font-size-sm)',
                                                    color: 'var(--labelsAndTextColor)',
                                                }}
                                            >
                                                {t("ConsoleTranslations.endDateLabel")}
                                            </p>
                                            <Calendar
                                                id="end-date-audit"
                                                key="start-date-audit"
                                                className="mb-3"
                                                showIcon
                                                icon="iu iu-icon-calendar"
                                                placeholder={language === 'es' ? "DD/MM/YYYY" : "MM/DD/YYYY"}
                                                dateFormat={ language === 'es' ? "dd/mm/yy" : "mm/dd/yy"}
                                                value={period.endDate.value}
                                                onChange={(e: any) => {
                                                    setPeriod((prev)=>({
                                                        ...prev,
                                                        endDate: {
                                                            ...prev.endDate,
                                                            value: e.value,
                                                        }
                                                    }));
                                                }}
                                                hourFormat="12"
                                                minDate={period.endDate.minDate}
                                                maxDate={period.endDate.maxDate}
                                                disabled={!period.startDate.value}
                                            />
                                        </div>
                                    }

                                    {
                                        permissionsInfoByRole.READ &&
                                        <>
                                            <Controller
                                                name='tenant'
                                                control={control}
                                                rules={AuditReportFormSchema(t).tenant}
                                                render={({ field }) => {
                                                    field.onChange = (e) => {
                                                        if (!!sessionStorage.getItem('INIT::FILTER')) {
                                                            sessionStorage.removeItem('INIT::FILTER');
                                                        };
                                                        setValue('tenant', e.value, REVALIDATE_FORM_PROPS);
                                                        const tenantAlias = tenantOptions?.filter(
                                                            (tenant:IMultiselectOptions) => e.value.includes(tenant.label))
                                                            ?.map((tenant:IMultiselectOptions)=>tenant.alias)
                                                        setValue('name', (()=>{
                                                            setNameAndRoleOptionsOrigin((prev)=>({
                                                                ...prev,
                                                                name: nameOptions.filter(
                                                                    (user:IMultiselectOptions)=>tenantAlias.includes(user?.label?.split(' - ')[1]))
                                                            }));
                                                            setNumSelectedItems((prev)=>({
                                                                ...prev,
                                                                name: 0,
                                                            }));
                                                            setError('name', { message: t("GlobalConfigurations.requiredErrorMessage") });
                                                            return []
                                                        })(), REVALIDATE_FORM_PROPS);
                                                        
                                                        setValue('role', (()=>{
                                                            setNameAndRoleOptionsOrigin((prev)=>({
                                                                ...prev,
                                                                role: roleOptions.filter(
                                                                    (role:IMultiselectOptions)=>tenantAlias.includes(role.label.split(' - ')[1]))
                                                            }));
                                                            setNumSelectedItems((prev)=>({
                                                                ...prev,
                                                                role: 0,
                                                            }));
                                                            setError('role', { message: t("GlobalConfigurations.requiredErrorMessage") });
                                                            return undefined
                                                        })(), REVALIDATE_FORM_PROPS);

                                                        setNumSelectedItems((prev: InumSelectedItems)=>({
                                                            ...prev,
                                                            tenant: e?.value?.length,
                                                        }));
                                                    };
                                                    return (
                                                    <>
                                                        <label htmlFor='tenant' className="block lb mt-2 mb-1">
                                                            {t(`ConsoleTranslations.tenantLabel`)}
                                                        </label>
                                                        <MultiSelect
                                                            options={tenantOptions}
                                                            optionLabel={'label'}
                                                            selectedItemTemplate={(value) => selectedItemTemplateForAllFields(value, t('ConsoleTranslations.tenantPlaceholderLabel'), (getValues('tenant') ?? []).length)}
                                                            className="w-full mb-2 audit-report-multiselect-class"
                                                            emptyFilterMessage={t("Tenants.noAvailableMessage")}
                                                            filter
                                                            maxSelectedLabels={3}
                                                            selectedItemsLabel={numSelectedItems.tenant === tenantOptions?.length ? t('ConsoleTranslations.allItemsLabel') : `${numSelectedItems.tenant} ${t('ConsoleTranslations.selectedItemsLabel')}`}
                                                            {...field}
                                                        />
                                                    </>
                                                    );
                                                }}
                                            />
                                            <ErrorMessage
                                                errors={errors}
                                                name="tenant"
                                                //@ts-ignore
                                                render={({ message }) => (
                                                    <small
                                                    className="p-error block"
                                                    style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                                                    >
                                                    {message}
                                                    </small>
                                                )}
                                            />
                                        </>
                                    }

                                    <Controller
                                        name='name'
                                        control={control}
                                        rules={AuditReportFormSchema(t).name}
                                        render={({ field }) => {
                                            field.onChange = (e) => {
                                                if (!!sessionStorage.getItem('INIT::FILTER')) {
                                                    sessionStorage.removeItem('INIT::FILTER');
                                                };
                                                setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
                                                setNumSelectedItems((prev: InumSelectedItems)=>({
                                                    ...prev,
                                                    name: e?.value?.length,
                                                }));
                                            };
                                            return permissionsInfoByRole.READ ? (
                                            <>
                                                <label htmlFor='name' className="block lb mt-2 mb-1">
                                                    {t(`ConsoleTranslations.nameLabel`)}
                                                </label>
                                                <MultiSelect
                                                    options={nameAndRoleOptionsOrigin.name}
                                                    optionLabel={'label'}
                                                    selectedItemTemplate={(value)=>selectedItemTemplateForAllFields(value, t('ConsoleTranslations.namePlaceholderLabel'), (getValues('name') ?? []).length, '-12px')}
                                                    className="w-full mb-2 audit-report-multiselect-class"
                                                    emptyFilterMessage={t("Tenants.noAvailableMessage")}
                                                    filter
                                                    maxSelectedLabels={3}
                                                    selectedItemsLabel={numSelectedItems.name === nameAndRoleOptionsOrigin.name?.length ? t('ConsoleTranslations.allItemsLabel') : `${numSelectedItems.name} ${t('ConsoleTranslations.selectedItemsLabel')}`}
                                                    {...field}
                                                />
                                            </>
                                            ) :
                                            (<>
                                                <label htmlFor='name' className="block lb mt-2 mb-1">
                                                    {t(`ConsoleTranslations.nameLabel`)}
                                                </label>
                                                <InputText
                                                    placeholder={t('ConsoleTranslations.nameFilterPlaceholder')} 
                                                    autoComplete="off"
                                                    type={'text'}
                                                    className="mb-2"
                                                    isReadOnly
                                                    {...field}
                                                />
                                            </>)
                                        }}
                                    />
                                    <ErrorMessage
                                        errors={errors}
                                        name="name"
                                        //@ts-ignore
                                        render={({ message }) => (
                                            <small
                                            className="p-error block"
                                            style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                                            >
                                            {message}
                                            </small>
                                        )}
                                    />

                                    <Controller
                                        name='role'
                                        control={control}
                                        rules={AuditReportFormSchema(t).role}
                                        render={({ field }) => {
                                            field.onChange = (e) => {
                                                if (!!sessionStorage.getItem('INIT::FILTER')) {
                                                    sessionStorage.removeItem('INIT::FILTER');
                                                };
                                                setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
                                                setNumSelectedItems((prev: InumSelectedItems)=>({
                                                    ...prev,
                                                    role: e?.value?.length,
                                                }));
                                            };
                                            return (
                                            <>
                                                <label htmlFor='role' className="block lb mt-2 mb-1">
                                                    {t(`ConsoleTranslations.roleLabel`)}
                                                </label>
                                                <MultiSelect
                                                    options={nameAndRoleOptionsOrigin.role}
                                                    optionLabel={'label'}
                                                    selectedItemTemplate={(value)=>selectedItemTemplateForAllFields(value, t('ConsoleTranslations.rolePlaceholderLabel'), (getValues('role') ?? []).length)}
                                                    className="w-full mb-2 audit-report-multiselect-class"
                                                    emptyFilterMessage={t("Tenants.noAvailableMessage")}
                                                    filter
                                                    maxSelectedLabels={3}
                                                    selectedItemsLabel={numSelectedItems.role === nameAndRoleOptionsOrigin.role?.length ? t('ConsoleTranslations.allItemsLabel') : `${numSelectedItems.role} ${t('ConsoleTranslations.selectedItemsLabel')}`}
                                                    {...field}
                                                />
                                            </>
                                            );
                                        }}
                                    />
                                    <ErrorMessage
                                        errors={errors}
                                        name="role"
                                        //@ts-ignore
                                        render={({ message }) => (
                                            <small
                                            className="p-error block"
                                            style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                                            >
                                            {message}
                                            </small>
                                        )}
                                    />

                                    <Controller
                                        name='functionality'
                                        control={control}
                                        rules={AuditReportFormSchema(t).functionality}
                                        render={({ field }) => {
                                            field.onChange = (e) => {
                                                if (!!sessionStorage.getItem('INIT::FILTER')) {
                                                    sessionStorage.removeItem('INIT::FILTER');
                                                };
                                                setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
                                                setNumSelectedItems((prev: InumSelectedItems)=>({
                                                    ...prev,
                                                    functionality: e?.value?.length,
                                                }));
                                            };
                                            return (
                                            <>
                                                <label htmlFor='functionality' className="block lb mt-2 mb-1">
                                                    {t(`ConsoleTranslations.functionalityLabel`)}
                                                </label>
                                                <MultiSelect
                                                    options={functionalityOptions}
                                                    optionLabel={'label'}
                                                    selectedItemTemplate={(value) => selectedItemTemplateForAllFields(value, t('ConsoleTranslations.functionalityPlaceholderLabel'), (getValues('functionality') ?? []).length)}
                                                    className="w-full mb-2 audit-report-multiselect-class"
                                                    emptyFilterMessage={t("Tenants.noAvailableMessage")}
                                                    filter
                                                    maxSelectedLabels={3}
                                                    selectedItemsLabel={numSelectedItems.functionality === functionalityOptions?.length ? t('ConsoleTranslations.allItemsLabel') : `${numSelectedItems.functionality} ${t('ConsoleTranslations.selectedItemsLabel')}`}
                                                    {...field}
                                                />
                                            </>
                                            );
                                        }}
                                    />
                                    <ErrorMessage
                                        errors={errors}
                                        name="functionality"
                                        //@ts-ignore
                                        render={({ message }) => (
                                            <small
                                            className="p-error block"
                                            style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                                            >
                                            {message}
                                            </small>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            className="flex mt-5 mb-4 gap-6"
                            style={{
                                width: '70%',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <AccessButton
                                id="downloadreport-access-button"
                                icon="iu iu-icon-download"
                                label={t("ConsoleTranslations.downloadReportButton")}
                                onClick={() => handleModalProps("DOWNLOAD")}
                                disabled={!records || records?.length === 0}
                            />
                            <Button
                                variant="p-button-text"
                                label={t("ConsoleTranslations.resetFilterButton")}
                                onClick={() => {
                                    reset(defaultFormValues);
                                    setNameAndRoleOptionsOrigin({
                                        name: nameOptions,
                                        role: roleOptions,
                                    });
                                    setNumSelectedItems({
                                        tenant: tenantOptions.length,
                                        name: nameOptions.length,
                                        role: roleOptions.length,
                                        functionality: functionalityOptions.length,
                                    });
                                    setRecords(null);
                                    setTotalRecords(0);
                                    setFirst(0);
                                    setRows(10);
                                }}
                                disabled={!(
                                    periodValues !== defaultFormValues.period ||
                                    tenantValues?.toString() !== defaultFormValues.tenant?.toString() ||
                                    nameValues?.toString() !== defaultFormValues.name?.toString() ||
                                    roleValues?.toString() !== defaultFormValues.role?.toString() ||
                                    functionalityValues?.toString() !== defaultFormValues.functionality?.toString()
                                )}
                            />
                            <Button
                                variant="p-button"
                                label={t("ConsoleTranslations.filterButton")}
                                onClick={()=>{
                                    let allDataByField: {
                                        tenant: any[],
                                        role: any[],
                                        functionality: IMultiselectOptions[],
                                        name?: string[],
                                    } = {
                                        tenant: tenantOptions,
                                        role: nameAndRoleOptionsOrigin.role,
                                        functionality: functionalityOptions,
                                        name: nameAndRoleOptionsOrigin.name
                                    };
                                    
                                    const fieldsFormValuesObject = getValues();
                                    let body:{} = {};
                                    // eslint-disable-next-line array-callback-return
                                    Object.entries(fieldsFormValuesObject)?.map((arrayByField) => {
                                        // @ts-ignore
                                        body = {
                                            ...body,
                                            [arrayByField[0] === 'name'
                                                ? 'userId'
                                                :   ( arrayByField[0] === 'tenant'
                                                        ? 'tenantId'
                                                        : ( arrayByField[0] === 'role'
                                                            ? 'roleId'
                                                            : arrayByField[0]
                                                        )
                                                    )
                                                // @ts-ignore
                                            ]:  [...arrayByField[1]]?.map((elem:string)=>(allDataByField[arrayByField[0]]?.find(
                                                (item:{id: string, label: string, value: string})=>item.value === elem))?.id),
                                        };
                                    });

                                    let endDate: Date = new Date();
                                    let startDate: Date = new Date();
                                    if (fieldsFormValuesObject.period !== '5') {
                                        const today: Date = new Date();
                                        switch (fieldsFormValuesObject.period) {
                                            case '2':
                                                const dayOfWeek = today.getDay();
                                                const startOfLastWeek = new Date(today);
                                                startOfLastWeek.setDate(today.getDate() - dayOfWeek - 7);
                                                startOfLastWeek.setHours(0,0,0,0);
                                                const endOfLastWeek = new Date(startOfLastWeek);
                                                endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
                                                endOfLastWeek.setHours(23,59,59,999);
                                                
                                                startDate = startOfLastWeek;
                                                endDate = endOfLastWeek;
                                                break;
                                            case '3':
                                                const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                                                startOfLastMonth.setHours(0,0,0,0);
                                                const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                                                endOfLastMonth.setHours(23,59,59,999);

                                                startDate = startOfLastMonth;
                                                endDate = endOfLastMonth;
                                                break;
                                            case '4':
                                                const startOfLast30Days = new Date(today);
                                                startOfLast30Days.setDate(today.getDate() - 30);
                                                startOfLast30Days.setHours(0,0,0,0);

                                                startDate = startOfLast30Days;
                                                break;
                                            default:
                                                startDate.setDate(today.getDate() - 7);
                                                startDate.setHours(0,0,0,0);
                                                break;
                                        };
                                    }
                                    else {
                                        startDate = (period.startDate.value) ?? startDate;
                                        endDate = (period.endDate.value) ?? endDate;
                                    };
                                    
                                    body= {
                                        ...body,
                                        startCreationDate: startDate.toISOString().slice(0,-1).replace('T', ' '),
                                        endCreationDate: endDate.toISOString().slice(0,-1).replace('T', ' '),
                                        period: undefined,
                                    };

                                    setDataToReport(body);

                                    setLoading(true);
                                    (async ()=>{
                                        const rowsPerPage: number = 10;
                                        !records ? onPage(undefined, body)
                                            : (() => {
                                                setFirst(0);
                                                setRows(rowsPerPage);
                                            })();
                                    })();
                                    setLoading(false);
                                }}
                                disabled={disableButton || !!records}
                            />
                        </div>
                    </BoxFull>
                    {!!records && (
                        <div id="wrapper-auditory-report">
                            <BoxFull className="dt-c mb-4">
                                <DataTable
                                    header={
                                        <TableHeader
                                            title={t("ConsoleTranslations.auditReportTitle")}
                                            columns={columns}
                                            selectedColumns={selectedColumns}
                                            toggleColumns={setSelectedColumns}
                                            filters={[]}
                                            setFilters={()=>{}}
                                            showInputSearch={false}
                                        />
                                    }
                                    currentPageReportTemplate={t(
                                    "ConsoleTranslations.rowsPaginatorTemplateLabel"
                                    )}
                                    paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
                                    first={first}
                                    totalRecords={totalRecords}
                                    lazy
                                    loading={loading}
                                    onPage={(event) => onPage(event)}
                                    emptyMessage={<EmptyTemplate image={noResultsImage} text={t('Catalogue.emptyDataTableMessage')} />}
                                    rows={rows}
                                    rowsPerPageOptions={[10, 20, 50]}
                                    size="normal"
                                    editMode="row"
                                    dataKey="id"
                                    sortField="functionality"
                                    sortOrder={1}
                                    value={records?.map((register:any)=>{
                                        return {
                                            ...register,
                                            date: formatDateToLocale(register?.creationDate, language , true),
                                            tenant: register?.tenant,
                                            application: register?.application,
                                            name: register?.userName,
                                            username: register?.userEmail,
                                            actions: (
                                                <Actions
                                                    viewButtonDelete={false}
                                                    enableEdit={false}
                                                    onDetail={async ()=>{
                                                        const { data } = await getAuditReportDetail(register.eventId, language);
                                                        setShowDetail(true);
                                                        setSelectedAuditoryRegister(data);
                                                    }}
                                                />
                                            ),
                                        }
                                    })}
                                    scrollable
                                    resizableColumns
                                    // Ojo, si se habilita la prop "stateKey", se corromperá la lógica de los
                                    // filtros, pues se vincula los botones del form de filtros con la DTable
                                    // stateKey='filters-table'
                                >
                                    {selectedColumns.map((col) => (
                                        <Column
                                            key={col.field}
                                            field={col.field}
                                            header={col.header}
                                            sortable={!(col.field === "actions")}
                                            align={col.field === "actions" ? "center" : "left"}
                                            headerStyle={col.field === "actions"
                                            ? {
                                                justifyContent: "center",
                                                // display: (!permissionsInfoByRole.UPDATE && !permissionsInfoByRole.REMOVE) ? 'none' : undefined,
                                                }
                                            : undefined }
                                            style={{
                                            boxShadow: (col.field === "actions")
                                                ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                                                : undefined,
                                            maxWidth: (col.field === "actions") ? "132px" : undefined,
                                            whiteSpace: "normal",
                                            // display: (col.field === "actions")
                                            //     ? ((!permissionsInfoByRole.UPDATE && !permissionsInfoByRole.REMOVE) ? 'none' : undefined)
                                            //     : undefined,
                                            }}
                                            frozen={col.field === "actions" ? true : false}
                                            alignFrozen={col.field === "actions" ? "right" : undefined}
                                            // body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'createdby')}
                                        />
                                    ))}
                                </DataTable>
                            </BoxFull>
                        </div>
                    )}
                </>
                :   <AuditoryDetail
                        data={selectedAuditoryRegister}
                        setShowDetail={setShowDetail}
                        t={t}
                        permissionsInfoByRole={permissionsInfoByRole}
                        language={language}
                        toast={toast}
                    />
            }
            <CustomDialog
                title={modalProps.title}
                visible={modalProps.visible}
                content={modalProps.content}
                handleModalState={setModalProps}
                onConfirm={modalProps.onConfirm}
                acceptLabel={modalProps.acceptLabel}
                rejectLabel={modalProps.rejectLabel}
                onReject={modalProps.onReject}
            />
        </>
    )
};
